import { useTranslation } from "react-i18next";
import "./UwDmvicCerts.scss";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import newRequest from "../../../utils/newRequest";
import { motion } from "framer-motion";
import { GridLoader, ScaleLoader } from "react-spinners";
import { IoArrowRedo, IoDocumentTextSharp } from "react-icons/io5";
import CircularButton, { CustomButtonSmallDarkBg, CustomLoadingButton } from "../../../components/ui/CustomButton";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports";
import { IoIosRefresh } from "react-icons/io";
import { MdAdd, MdArrowBackIos, MdClose, MdDownload, MdEmail, MdOutlineNumbers, MdPhone } from "react-icons/md";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    TablePagination,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import { getCssValue, initCap } from "../../../utils/genericFunctions";
import { FiSearch } from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import PdfPreview from "../../../components/pdfPreview/PdfPreview";
import InitialsAvatar from "../../../components/InitialsAvatar/InitialsAvatar";

const UwDmvicCerts = () => {
    const { t } = useTranslation();
    const socket = useSelector((state) => state.socket.socket);
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isProcessingTitle, setIsProcessingTitle] = useState(t('pleaseWait'));
    const navigate = useNavigate();
    const location = useLocation();
    const parts = location.pathname.split("/");
    const showBack = location.state?.showBack || "N";
    const [reportGroupArray, setReportKeysArray] = useState(["all-certificates"]);
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentCertificatePage, setCurrentCertificatePage] = useState(0);
    const [pdfPreviewDialogOpen, setPdfPreviewDialogOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [binaryDocid, setBinaryDocid] = useState(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailData, setEmailData] = useState(null);

    const handleCertificatePageChange = (event, newPage) => {
        setCurrentCertificatePage(newPage);
    };

    const handleCertificateRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentCertificatePage(0);
    };

    const filteredCertificates = certificates?.filter(
        (row) =>
            row?.cert_no?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            row?.cert_policy_no?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            row?.cert_regn_no?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            row?.insured_name_xx?.toLowerCase().includes(searchTerm?.toLowerCase())
    );

    const paginatedCertificates = filteredCertificates?.slice(
        currentCertificatePage * rowsPerPage,
        currentCertificatePage * rowsPerPage + rowsPerPage
    );


    const handleSearchCertificates = async () => {
        try {
            setIsLoading(true);
            if (searchTerm) {
                const res = await newRequest.post("/dmvic/search", { search_input: searchTerm });
                setCertificates(res.data);
            } else {
                fetchCertificates();
            }
        } catch (error) {
            console.error("Error searching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCertificates = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.post("/dmvic/findAll", { cert_risk_docid: "", result_limit: 20 });
            setCertificates(res.data);
            setIsLoading(false);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchCertificates();
    }, []);

    return (
        <motion.div
            className="dmvic-certs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader className="scaler-loader" />
                </div>
            ) : (
                <div className="list-main-wrapper">
                    <div className="navbar">
                        <div className="navbar-left">
                            <h1 className="main-title">{t('certificates')}</h1>
                        </div>
                        <div className="navbar-right">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#2563EB"
                                tooltip="Run reports for this document"
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                {t('reports')}
                            </CustomLoadingButton>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#0f766e5d"
                                endIcon={<IoIosRefresh />} onClick={fetchCertificates}>
                                {t('refresh')}
                            </CustomLoadingButton>
                            {/* This part shows if the menu was opened from home screen */}
                            {showBack === "Y" && <hr className="separator-v" />}
                            {showBack === "Y" &&
                                <span className="back-icon" onClick={() => navigate(-1)}>
                                    <MdArrowBackIos size={24} />
                                </span>}
                        </div>
                    </div>
                    <div className="dmvic-certs-wrapper">
                        <div className="content-wrapper">
                            <div className="table-content">
                                <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                    <Table sx={{ width: "max-content", border: 'none' }}>
                                        <TableHead className="tablehead">
                                            <TableRow className="tablerow">
                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                    #
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                    <span>{t('certNo').toUpperCase()}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                    <span>{t('regnNo').toUpperCase()}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                    <div className="search-input-wrapper" style={{ width: "100%" }}>
                                                        <FiSearch className="search-input-icon" />
                                                        <input
                                                            className="search-input"
                                                            type="text"
                                                            placeholder={`${t('search')} ${t('certificate')}`}
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSearchCertificates();
                                                            }}
                                                        />
                                                        {searchTerm?.length > 0 && (
                                                            <MdClose
                                                                onClick={async (e) => {
                                                                    try {
                                                                        setSearchTerm("");
                                                                        await fetchCertificates();
                                                                    } catch (error) {
                                                                        console.log(error);
                                                                    }
                                                                }}
                                                                className="search-input-icon"
                                                            />
                                                        )}
                                                        {searchTerm?.length === 0 && (
                                                            <span className="search-input-icon" />
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                    <span></span>
                                                </TableCell>
                                                <TableCell className="table-cell center" style={{ maxWidth: "200px" }}>
                                                    <span>{t('status')}</span>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody className="tablebody">
                                            {paginatedCertificates?.map((row, index) => (
                                                <TableRow
                                                    key={row._id}
                                                    className={`${selectedCertificate?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                    onClick={() => setSelectedCertificate(row)}
                                                >
                                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                        {currentCertificatePage * rowsPerPage + index + 1}
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                        <span className="certificate-no"
                                                            onClick={async (e) => {
                                                                setSelectedCertificate(row);
                                                                try {
                                                                    const response = await newRequest.get(`/dmvic/viewCertificate/${row?.cert_no}`, {
                                                                        responseType: 'arraybuffer',
                                                                    });
                                                                    const blob = new Blob([response.data], { type: response.headers['content-type'] });
                                                                    const url = URL.createObjectURL(blob);
                                                                    setPdfUrl(url);
                                                                    setPdfPreviewDialogOpen(true);
                                                                } catch (error) {
                                                                    console.log(error)
                                                                }
                                                            }}
                                                        >{row?.cert_no}</span>
                                                        {/* <CustomButtonSmallDarkBg
                                                            borderRadiusType='left'
                                                            currentTheme={currentTheme}
                                                            onClick={async (e) => {
                                                                setSelectedCertificate(row);
                                                                try {
                                                                    const response = await newRequest.get(`/dmvic/viewCertificate/${row?.cert_no}`, {
                                                                        responseType: 'arraybuffer',
                                                                    });
                                                                    const blob = new Blob([response.data], { type: response.headers['content-type'] });
                                                                    const url = URL.createObjectURL(blob);
                                                                    setPdfUrl(url);
                                                                    setPdfPreviewDialogOpen(true);
                                                                } catch (error) {
                                                                    console.log(error)
                                                                }
                                                            }}
                                                            startIcon={<IoArrowRedo />}
                                                            isCurrent={true}
                                                        >
                                                            {t('previewCertificate').toUpperCase()}
                                                        </CustomButtonSmallDarkBg> */}
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                        <span className="sub-title">{row?.cert_regn_no}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                        <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                                                            <div className='current-user-avatar' style={{ width: "100%", alignItems: "center", justifyContent: "flex-start" }}>
                                                                <div className="user-wrapper" style={{ width: "100%", alignItems: "center", justifyContent: "flex-start" }}>
                                                                    {row?.insured_photo_url_xx ? <img src={row?.insured_photo_url_xx} alt="" className='avatar' />
                                                                        :
                                                                        <InitialsAvatar
                                                                            name={row?.insured_name_xx}
                                                                            size={30}
                                                                            currentTheme={currentTheme}
                                                                        />
                                                                    }
                                                                    <div className="user-org-view" style={{ width: "100%" }}>
                                                                        <span className="sub-title nowrap">{row?.insured_name_xx} </span>
                                                                        <span style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", gap: "10px" }}>
                                                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "3px", flex: "1" }} >
                                                                                <MdOutlineNumbers className="icon" />  <h1 className="sub-title xrsmall">{row?.cert_assr_ent_code}</h1>
                                                                            </span>
                                                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "3px", flex: "2" }} >
                                                                                <MdEmail className="icon" />  <h1 className="sub-title xrsmall">{row?.insured_email_xx || "Unspecified"}</h1>
                                                                            </span>
                                                                            <span style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "3px", flex: "1" }} >
                                                                                <MdPhone className="icon" /> <h1 className="sub-title xrsmall">{row?.insured_phone_no_xx || "Unspecified"}</h1>
                                                                            </span>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        <div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "5px" }}>
                                                            <span className={`status ${getCssValue(row?.cert_type)}`} style={{ width: "2px" }} />
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                                <span className="sub-title">{row?.cert_type}</span>
                                                                <span className="sub-title soft" style={{ fontSize: "10px" }}>{row?.issued_time_ago_xx}</span>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell center" style={{ maxWidth: "200px" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", width: "100%" }}>
                                                            <span className={`sub-title xrsmall status ${getCssValue(row?.cert_status)}`}>{row?.cert_status}</span>
                                                            <span className="sub-title soft" style={{ fontSize: "10px" }}>{initCap(row?.issued_by_name_xx)}</span>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {certificates?.length > rowsPerPage &&
                                        <TablePagination
                                            rowsPerPageOptions={[10]}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleCertificateRowsPerPage}
                                            component="div"
                                            count={certificates?.length}
                                            rows={certificates}
                                            page={currentCertificatePage}
                                            onPageChange={handleCertificatePageChange}
                                        />}
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('reports')}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={reportGroupArray}
                                documentData={{
                                    docNo: "1",
                                    docId: "1",
                                    docType: "DMVIC Certificate",
                                    format: "xlsx",
                                    entity_code: "",
                                    date_fm: "",
                                    date_to: "",
                                    status: "",
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <span className='main-title'>{isProcessingTitle || t('pleaseWait')}</span>
                        <ScaleLoader color="#bd97fc"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>)}
            {pdfPreviewDialogOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper pdf">
                        <div className="overlay-navbar">
                            <div className="left" style={{ padding: "5px" }}>
                                <h1 className="main-title">{t('preview').toUpperCase()}</h1>
                            </div>
                            <div className="right">
                                {/* <CustomLoadingButton
                              currentTheme={currentTheme}
                              iconPosition="end"
                              borderRadiusType='left'
                              endIcon={<MdMarkEmailUnread />}
                              onClick={() => {
                                try {
                                  const urlString = pdfUrl.toString().replace("blob:", "");
                                  setEmailData({
                                    to: quoteData?.insured_details_xx?.ent_email,
                                    from: currentUser?.usr_org_name,
                                    cc: '',
                                    bcc: '',
                                    subject: `Certificate No ${quoteData?.pl_risk_note_no}`,
                                    show_attach: "N",
                                    // attached_urls: [{ id: 1, url: urlString, name: `${quoteData?.pl_type} No ${quoteData?.pl_risk_note_no}`, binary_docid: "" }],
                                    binary_docs: [{ doc_id: binaryDocid, doc_name: `${quoteData?.pl_type} No ${quoteData?.pl_risk_note_no}`, doc_type: "pdf", doc_url: "", doc_size_kb: 0 }],
                                    doc_no: quoteData?.pl_risk_note_no,
                                    doc_type: quoteData?.pl_type,
                                    doc_id: quoteData?._id,
                                    entity_codes: [quoteData?.insured_details_xx?.ent_code],
                                    search_string: quoteData?.insured_details_xx?.ent_name + "," + quoteData?.pl_risk_note_no,
                                    ...quoteData
                                  });
                                  setPdfPreviewDialogOpen(false);
                                  setEmailDialogOpen(true);
                                } catch (error) {
                                  console.log(error)
                                }
                              }}>
                              {t('email')}
                            </CustomLoadingButton> */}
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType='both'
                                    height={"36px"}
                                    endIcon={<MdDownload />}
                                    onClick={() => {
                                        try {
                                            const link = document.createElement('a');
                                            link.href = pdfUrl;
                                            link.download = `${selectedCertificate?.cert_no}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        } catch (error) {
                                            console.log(error)
                                        }
                                    }}>
                                    {t('download')}
                                </CustomLoadingButton>
                                <hr className='separator-v' />
                                <CircularButton currentTheme={currentTheme} onClick={() => setPdfPreviewDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                            </div>
                        </div>
                        <div className="overlay-content-pdf" >
                            <PdfPreview pdfUrl={pdfUrl} />
                        </div>
                        <div className="overlay-actions">
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}


export default UwDmvicCerts;